import { Dialog, Flex, FormStatus } from '@dnb/eufemia';
import { Field, Form } from '@dnb/eufemia/extensions/forms';
import { SCOPE_NAME_MATCHER } from '@portals/shared/common/matchers';
import { useCallback, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { createScopeCreationRequest } from '@/api/api';
import { ScopeCreationRequest } from '@/openapi/schema';

import { ApiProviderPageOutletContext } from '../..';

interface RequestNewScopeProps {
  apiSlug: string;
  onSuccess: () => void;
}

const INVALID_SCOPE_NAME = `The name must follow the format <prefix>.<resource>:<operation>
  Each section supports lowercase letters, numbers, hyphens and underscore.
  The resource section can also consist of multiple sub-sections, separated by periods (.)
  and contain at least one letter e.g prefix.resource1.resource2:operation`;

export default function RequestNewScope({ apiSlug, onSuccess }: RequestNewScopeProps) {
  const { isTeamAdmin, api } = useOutletContext<ApiProviderPageOutletContext>();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleCreateScopeRequest = useCallback(
    async (data: ScopeCreationRequest) => {
      setErrorMessage(null);
      const { error, response } = await createScopeCreationRequest(apiSlug, {
        name: data.name,
        description: data.description,
      });

      if (!response.ok) {
        setErrorMessage(error?.detail ?? 'Failed to create scope request. Please try again.');
        return;
      }

      setDialogOpen(false);
      onSuccess();
    },
    [apiSlug, onSuccess],
  );

  const shouldDisable = !isTeamAdmin || api.stage === 'decommissioned' || api.stage === 'deprecated';

  const tooltip = isTeamAdmin
    ? shouldDisable
      ? 'New scopes cannot be requested for deprecated or decommissioned APIs'
      : undefined
    : 'Only team admins can request new scopes';

  return (
    <Dialog
      noAnimation
      onClose={() => setDialogOpen(false)}
      open={dialogOpen}
      title="Request new scope"
      triggerAttributes={{
        text: 'Request new scope',
        onClick: () => {
          setErrorMessage(null);
          setDialogOpen(true);
        },
        disabled: shouldDisable,
        tooltip,
      }}
      verticalAlignment="top"
    >
      <Form.Handler<ScopeCreationRequest>
        defaultData={{ name: '', description: '' }}
        onSubmit={handleCreateScopeRequest}
      >
        <Flex.Vertical gap="small">
          <Field.String
            label="Name"
            onChangeValidator={(value) => {
              if (!SCOPE_NAME_MATCHER.test(value)) {
                return new Error(INVALID_SCOPE_NAME);
              }
              return;
            }}
            path="/name"
            placeholder="prefix.resource:action"
            required
          />
          <Field.String
            label="Description"
            path="/description"
            placeholder="What does the scope give access to?"
            required
          />
          {errorMessage && <FormStatus text={errorMessage} />}
          <Form.SubmitButton />
        </Flex.Vertical>
      </Form.Handler>
    </Dialog>
  );
}
